/**
 * Kick leg — Stage 4c integration (folded into Stage 2).
 *
 * Loads assets/models/leg_rig.glb (meshopt-compressed) and attaches two leg
 * instances to the camera (left + mirrored right). On a `kick` event, the
 * matching leg performs a ~583 ms procedural poke on an extend / HOLD /
 * retract envelope (18% / 28% / 54%): it SNAPS up from the bottom-of-frame
 * (hip off-screen) toward the event's contact-point direction in ~105 ms,
 * HOLDS ~165 ms at full extension with the shoe dead-centre of the frame
 * (NDC y ≈ 0), then drops, folds and FADES away over ~314 ms — exactly 3x the
 * snap out (owner playtest), so the eye reads the extension as the accent and
 * the return as a deliberate recovery. (Sinking a
 * straight leg at hold depth, as an earlier version did, let the opaque road
 * plane eat the thigh and calf and leave the shoe floating in mid-frame — see
 * the retract block in juice.ts.) A `kickWhiff` plays
 * the SAME animation at full extent (see `whiff`) — only the camera dip and the
 * impact juice are withheld. Legs are hidden when idle.
 *
 * DOUBLE KICK (sim/kick.ts `pickKickTargets`): the two instances are fully
 * independent — own groups, own material twins, own `LegState` — so the render
 * simply calls `kick()` twice, once per leg, each AIMED at its own target's
 * contact point. Nothing here is shared between them, and `warmup` poses all
 * four variants (left/right x opaque/fade), so a double kick compiles no
 * pipeline a single kick has not already paid for.
 *
 * The EXTEND phase is itself split into chamber (~42 ms, thigh swings the knee
 * up into the lower third of the frame while the shin folds ~112° and the shoe
 * tucks down to the bottom edge) and thrust (~63 ms, both joints SNAP straight as the
 * shoe shoots to frame centre) — see `pokePose` in juice.ts. The leg is a
 * two-bone FK rig (`kick_thigh` → `kick_shin`), not the old single rigid mesh:
 * without a real knee the poke reads as a knee-LOCKED plank swinging up.
 *
 * The poke is driven in camera-local space: the leg group is parented to
 * the camera, positioned off-screen at rest, and translated upward + forward
 * toward the contact point during extension. The target vehicle's CENTRE OF
 * MASS (event aimX/aimZ) is projected into a camera-local direction to aim the
 * poke: the leg group YAWS OUTWARD toward it (juice.ts `legAimYaw`, clamped to
 * JUICE.leg.aimYawMax) as it thrusts — on a double kick the two legs therefore
 * splay into a V, one per vehicle. That yaw turns the wrapper about the leg's
 * MIDDLE, so `poseLegWrapper` also slides the wrapper by
 * `legAimHipShiftX(yaw)`, moving the pivot to the HIP; without it the hip
 * swings across the body and the double kick reads as an X, not a V (owner
 * playtest 2026-08-02 — see the AIM PIVOT block in JUICE.leg). The dust/spark
 * burst still fires at the
 * CONTACT point (the near corner the foot actually meets); the leg points at
 * the body. Extension is scaled by the contact distance against the
 * rig's own maximum reach (JUICE.leg.reachMax). A whiff has no target and pokes
 * straight at screen centre.
 *
 * Uses three/webgpu-compatible loading: GLTFLoader from three/addons, which
 * imports from the main `three` module (shared class identity with
 * three/webgpu). MeshoptDecoder is set for compressed GLBs.
 */

import {
  Group,
  Object3D,
  Mesh,
  type Material,
  type BufferGeometry,
} from "three/webgpu";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/addons/libs/meshopt_decoder.module.js";
import {
  JUICE,
  pokePose,
  clamp01,
  legAimYaw,
  legAimHipShiftX,
  type PokePose,
} from "./juice";
import { plasticify } from "./plastic";

const L = JUICE.leg;

/** Scratch wrapper pose reused every frame (zero per-frame allocation). */
const poseScratchWrapper: LegWrapperPose = {
  reach: 0,
  lift: 0,
  tilt: 0,
  aimYaw: 0,
  aimSlide: 0,
};

/** Scratch pose reused every frame (zero per-frame allocation). */
const poseScratch: PokePose = {
  reach: 0,
  lift: 0,
  tilt: 0,
  thighRot: 0,
  shinRot: 0,
  shinYaw: 0,
  alpha: 1,
};

/** Node names in assets/models/leg_rig.glb (see ASSET_PIPELINE.md). */
const THIGH_NODE = "kick_thigh";
const SHIN_NODE = "kick_shin";

/**
 * Yaw applied to the leg GLB inside its animated wrapper. The model is
 * authored with the SHOE on local +Z and the hip on local -Z, i.e. the foot
 * points back at the camera. Verified in-browser by parenting a probe clone
 * to the camera rotated 90° about Y: the shoe appeared on the +X side.
 * A 180° yaw turns the foot down-road (-Z) so the poke actually kicks
 * forward, with the hip end trailing back under the bottom edge of frame.
 */
const LEG_YAW = Math.PI;

/**
 * The wrapper-transform inputs of one poke frame — the normalized channels of
 * {@link PokePose} plus this kick's aim. Split out of `updateLeg` so the
 * composed node transforms can be exercised headless (kickLegAim.test.ts)
 * without a renderer or the GLB.
 */
export interface LegWrapperPose {
  /** 0 = rest depth, 1 = full extension (already scaled by `extent`). */
  reach: number;
  /** 0 = rest height, 1 = full extension (already scaled by `extent`). */
  lift: number;
  /** Multiplier on `pokeTiltRad` (already scaled by `extent`). */
  tilt: number;
  /** Aim yaw at FULL thrust (juice.ts `legAimYaw`); winds in with `reach`. */
  aimYaw: number;
  /** Normalized lateral aim (-1..1) driving the small hip slide. */
  aimSlide: number;
}

/**
 * Build the two-node rig kickLeg parents to the camera: `model` (mirrored on
 * the right, turned foot-down-road) inside a fresh wrapper Group parked at the
 * rest pose. Exported so tests can compose the same hierarchy over a stand-in
 * model — the mirror lives HERE, one level below the wrapper the aim yaw is
 * written to (see the sign convention at juice.ts `legAimYaw`).
 */
export function buildLegWrapper(model: Object3D, side: "left" | "right"): Group {
  // Mirror the right leg by negating x — y/z must still carry the uniform
  // scale, or the mirrored leg comes out short and thin.
  model.scale.set(side === "right" ? -L.scale : L.scale, L.scale, L.scale);
  // Turn the foot down-road. This lives on an INNER node so `poseLegWrapper`
  // can own the wrapper's rotation.x (the poke tilt) without fighting the yaw.
  model.rotation.y = LEG_YAW;
  const wrap = new Group();
  wrap.name = `leg_${side}`;
  wrap.add(model);
  // Rest pose: whole leg parked below the frame, foot already ahead.
  wrap.position.set(legBaseX(side), L.restY, L.restZ);
  wrap.visible = false;
  return wrap;
}

/** This leg's resting camera-local x (the rider's hip, and the yaw pivot). */
function legBaseX(side: "left" | "right"): number {
  return side === "left" ? -L.lateralOffset : L.lateralOffset;
}

/**
 * Write one poke frame onto a wrapper built by {@link buildLegWrapper}.
 *
 * THREE THINGS HAPPEN TO THE WRAPPER'S X, and only the first is obvious:
 *  - the base hip offset,
 *  - a small SLIDE toward the target (`aimSlide`),
 *  - the HIP-PIVOT SHIFT (juice.ts `legAimHipShiftX`), which cancels the
 *    hip displacement `rotation.y` would otherwise cause. Without it the yaw
 *    turns the leg about its middle and throws the hip across the body — the
 *    crossed double kick of 2026-08-02. See the AIM PIVOT block in JUICE.leg.
 */
export function poseLegWrapper(
  wrap: Object3D,
  side: "left" | "right",
  p: LegWrapperPose,
): void {
  // Interpolate from rest (hip off-screen below) to extended (toward contact).
  // `lift` and `reach` are separate channels so the chamber can raise the
  // knee while the shoe is still low and close to the camera.
  const y = L.restY + (L.extendY - L.restY) * p.lift;
  // Yaw NOW: the aim winds in with the thrust and unwinds along the retract's
  // own `reach` curve (so the return path's shape is unchanged, only its
  // heading).
  const yawNow = p.aimYaw * p.reach;
  // DEPTH COMPENSATION. Yawing the wrapper swings the shoe sideways but also
  // pulls it NEARER the camera by (1 - cos yaw) of the leg's arm, and a near
  // object at a big lateral offset projects hard toward the frame edge.
  // Pushing the wrapper back by 1/cos(yaw) restores the shoe's FORWARD
  // distance, which is also the honest reading of the pose (the leg reaches
  // just as far, at an angle), and buys back the screen room the yaw spent.
  // Sign-neutral (cos is even) and no effect on a straight-ahead poke
  // (cos 0 = 1) or on the shoe's HEIGHT (a Y rotation preserves y, so the hold
  // still parks the shoe at NDC y ≈ 0).
  const z = (L.restZ + (L.extendZ - L.restZ) * p.reach) / Math.cos(yawNow);
  // Lateral: hip offset + a small SLIDE of the whole leg toward the target +
  // the hip-pivot shift (see the doc comment).
  const x =
    legBaseX(side) +
    p.aimSlide * L.aimSlide * p.reach +
    legAimHipShiftX(yawNow);
  wrap.position.set(x, y, z);

  // Tilt: the foot swings UP relative to the hip as the leg extends, so the
  // shoe climbs toward mid-frame while the hip end stays under the bottom
  // edge. (+rotation.x lifts the -Z end of the model.) The wrapper stays
  // LEVEL through the chamber — raising the knee is the thigh joint's job.
  wrap.rotation.x = p.tilt * L.pokeTiltRad;
  // Aim yaw (see `yawNow` above, which the depth compensation shares).
  wrap.rotation.y = yawNow;
}

/**
 * Multi-pass leg warm-up handle (see {@link KickLeg.warmup}). Mirrors the
 * vehicle and biome warm-up handles: the caller runs `passes` iterations of
 * `beginPass(p)` + one real frame, then `restore`.
 */
export interface LegWarmup {
  /** Number of passes the caller must render (>= 1). */
  readonly passes: number;
  /** Pose the legs for pass `p` (0-based). */
  beginPass(p: number): void;
  /** Hide the legs again and hand them back to `update`. */
  restore(): void;
}

interface LegState {
  group: Group;
  /**
   * The meshes of this leg, parallel to `opaqueMats` / `fadeMats`. The retract
   * fade SWAPS the material reference on these rather than mutating one
   * material's `transparent` flag — see `setAlpha`.
   */
  meshes: Mesh[];
  /**
   * This leg's OWN opaque materials (index-parallel to `meshes`).
   * `Object3D.clone` shares materials with the template (and therefore between
   * the two legs), so the retract fade would dissolve both legs at once — and
   * permanently, since nothing else writes opacity. Each leg gets its own
   * clones instead.
   */
  opaqueMats: Material[];
  /**
   * The same materials with `transparent = true`, pre-built at LOAD time and
   * pre-compiled by the warm-up pass.
   *
   * PERFORMANCE (2026-08-01 kick-hitch pass). The fade used to flip
   * `transparent` on the ONE material set and set `needsUpdate`. Under
   * WebGPURenderer `transparent` is part of the render object's material cache
   * key, so every flip made three dispose the render object and rebuild it —
   * and `RenderObjects.onDispose` releases the compiled PIPELINE with it once
   * its use count hits zero. The leg therefore recompiled its shaders TWICE per
   * kick (opaque -> transparent on the retract, transparent -> opaque at the
   * end), inside the render submit, which is exactly where the playtest's
   * 25-90 ms spikes landed. Measured: 11 frames over 20 ms across 12 kicks
   * before, 0 after.
   *
   * Two material objects means two render objects, two pipelines, both resident
   * for the whole run: swapping `mesh.material` between them costs nothing and
   * neither material's cache key ever changes. Visually identical — the fade
   * material is a straight clone, so the same sizes/colours/timing survive.
   */
  fadeMats: Material[];
  /** Whether the meshes currently carry `fadeMats` (i.e. alpha < 1). */
  fading: boolean;
  /** Last opacity written, so the fade only touches materials when it changes. */
  alpha: number;
  /** `kick_thigh` node (pivot = hip); null if the GLB is not rigged. */
  thigh: Object3D | null;
  /** `kick_shin` node (pivot = knee, child of the thigh); null if unrigged. */
  shin: Object3D | null;
  /** Current poke animation progress 0..1 (1 = fully extended, retracting after). */
  elapsed: number;
  duration: number;
  extent: number; // 0..1 reach fraction (1 full, <1 for whiff)
  active: boolean;
  /** Fraction of `duration` spent extending. */
  extendFrac: number;
  /** Fraction of `duration` held at full extension. */
  holdFrac: number;
  /** Target lateral aim direction (-1 left .. +1 right) for this poke. */
  aimX: number;
  /**
   * Yaw (rad, camera-local) the whole leg turns toward the contact point during
   * chamber + thrust. 0 on a whiff (straight at screen centre).
   */
  aimYaw: number;
  /** Whether this is a whiff (no contact dip). */
  whiff: boolean;
}

/**
 * Kick-leg controller. Construct after the camera exists; call `attach` to
 * parent the legs to the camera. Call `kick(leg, contactX, contactZ)` /
 * `whiff(leg)` from event handlers; call `update(dt)` each render frame.
 */
export class KickLeg {
  private left!: LegState;
  private right!: LegState;
  private readonly geometries: BufferGeometry[] = [];
  private readonly materials: Material[] = [];
  private loaded = false;
  /** Camera dip impulse (additive pitch on connect). */
  private dipElapsed = 0;
  private dipDuration = 0;
  private dipActive = false;

  /**
   * Load the leg GLB and create two mirrored instances.
   * @returns a promise that resolves when both legs are built.
   */
  load(): Promise<void> {
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    return new Promise<void>((resolve, reject) => {
      loader.load(
        L.loadPath,
        (gltf) => {
          const template = gltf.scene as Group;
          // Normalize: center + scale to a reasonable size.
          // Collect FIRST (this registers the GLB's own PBR materials), then
          // convert: `plasticify` returns the freshly created toy-plastic
          // materials, which must also be disposed on teardown.
          this.collectDisposables(template);
          // curve: false — the legs are parented to the camera; the world bend
          // (render/earthCurve.ts) is for world-space geometry only.
          this.materials.push(...plasticify(template, false));
          this.left = this.buildLeg(template, "left");
          this.right = this.buildLeg(template, "right");
          this.loaded = true;
          resolve();
        },
        undefined,
        (err) => reject(err),
      );
    });
  }

  /** Parent both leg groups to the camera (call once after camera exists). */
  attach(camera: Object3D): void {
    if (!this.loaded) return;
    camera.add(this.left.group);
    camera.add(this.right.group);
    this.left.group.visible = false;
    this.right.group.visible = false;
  }

  /**
   * Trigger a kick poke on the matching leg, AIMED at the target vehicle's
   * centre of mass (the contact point only scales the extension).
   *
   * @param leg      "left" | "right" (from the sim kick event).
   * @param dxLocal  contact X RELATIVE TO THE PLAYER (m). The legs are camera
   *   children and the rig tracks the player laterally, so the player — not the
   *   world origin — is the leg's frame of reference; passing the event's
   *   absolute `contactX` aimed the leg at the wrong car whenever the player
   *   was off centre.
   * @param contactZ sim-space contact Z (negative, ahead) — sets the extension
   *   scale.
   * @param aimDxLocal target vehicle's CENTRE-OF-MASS x relative to the player
   *   (m). Defaults to `dxLocal` for callers/events without an aim point. This
   *   — not the contact corner — is what the thrust direction points at; see
   *   the aim block in juice.ts JUICE.leg.
   * @param aimZ   target vehicle's centre-of-mass sim z. Defaults to
   *   `contactZ`.
   */
  kick(
    leg: "left" | "right",
    dxLocal: number,
    contactZ: number,
    aimDxLocal: number = dxLocal,
    aimZ: number = contactZ,
  ): void {
    if (!this.loaded) return;
    const s = leg === "left" ? this.left : this.right;
    const contactX = dxLocal;
    s.elapsed = 0;
    s.duration = L.pokeDuration;
    s.extendFrac = L.pokeExtendFrac;
    s.holdFrac = L.pokeHoldFrac;
    s.extent = 1;
    s.active = true;
    this.setAlpha(s, 1);
    s.whiff = false;
    // Lateral slide of the whole leg toward the target's BODY (not the contact
    // corner), so the hip drifts outward with the thrust as well.
    s.aimX = Math.max(-1, Math.min(1, aimDxLocal * 0.3));
    // AIM: the wrapper yaw that puts the shoe on the camera→centre-of-mass ray
    // (juice.ts `legAimYaw` — pure and unit-tested; it also owns the sign,
    // which three's Ry makes counter-intuitive). This is what makes the legs
    // splay OUTWARD in a V on a double kick instead of both poking forward.
    s.aimYaw = legAimYaw(aimDxLocal, aimZ);
    // EXTENSION: scaled by how far the contact actually is, clamped to the
    // rig's own maximum reach so the leg is never hyper-extended. Every kick
    // the sim allows is beyond full reach (window 4.5-9.1 m vs 2.75 m of leg),
    // so in practice this is full extension — see L.reachMax for the reasoning
    // and the compromise it documents.
    const contactDist = Math.hypot(contactX, contactZ);
    s.extent = Math.max(L.minExtent, Math.min(1, contactDist / L.reachMax));
    // Trigger the camera dip.
    this.dipActive = true;
    this.dipElapsed = 0;
    this.dipDuration = L.cameraDipDuration;
  }

  /**
   * Trigger a whiff poke on the given leg.
   *
   * IDENTICAL to a connecting kick's leg animation (owner decision): same
   * `pokePose` chamber→thrust envelope, same `pokeDuration`, FULL extent, same
   * hold with the shoe parked at frame centre. A whiff used to play a shorter
   * (0.16 s), hold-less, half-extent stab, which made a missed kick read as "the
   * input didn't register" rather than "I missed" — the player could not tell a
   * whiff from a dropped press. The only thing a whiff still does not do is the
   * camera dip, which is an IMPACT cue, not part of the leg animation.
   *
   * Leg choice is the caller's (juiceCoordinator alternates left/right, since
   * the sim sets `lastKickLeg = null` on a whiff).
   */
  whiff(leg: "left" | "right"): void {
    if (!this.loaded) return;
    const s = leg === "left" ? this.left : this.right;
    s.elapsed = 0;
    s.duration = L.pokeDuration;
    s.extendFrac = L.pokeExtendFrac;
    s.holdFrac = L.pokeHoldFrac;
    s.extent = 1;
    s.active = true;
    this.setAlpha(s, 1);
    s.whiff = true;
    s.aimX = leg === "left" ? -0.5 : 0.5;
    // No target to aim at: the poke goes straight at screen centre, exactly as
    // it did before the aiming pass.
    s.aimYaw = 0;
  }

  /** Advance leg animations by `dt` render seconds. */
  update(dt: number): void {
    if (!this.loaded) return;
    this.updateLeg(this.left, dt);
    this.updateLeg(this.right, dt);

    if (this.dipActive) {
      this.dipElapsed += dt;
      if (this.dipElapsed >= this.dipDuration) {
        this.dipActive = false;
      }
    }
  }

  /**
   * Apply the camera dip (additive pitch) — call AFTER rig.update, BEFORE
   * or alongside screen shake. Returns the dip angle in radians to add to
   * camera.rotation.x.
   */
  getDipRad(): number {
    if (!this.dipActive) return 0;
    const p = clamp01(this.dipElapsed / this.dipDuration);
    // Bell curve: 0 → 1 → 0.
    const bell = Math.sin(p * Math.PI);
    return bell * L.cameraDipDeg * (Math.PI / 180);
  }

  reset(): void {
    if (this.left) {
      this.left.active = false;
      this.left.group.visible = false;
      // Killing a poke mid-retract would otherwise leave the leg's materials
      // parked at whatever opacity the fade had reached.
      this.setAlpha(this.left, 1);
    }
    if (this.right) {
      this.right.active = false;
      this.right.group.visible = false;
      this.setAlpha(this.right, 1);
    }
    this.dipActive = false;
  }

  /**
   * Pose both legs ON SCREEN for the LOADING-time warm-up frames, so every
   * pipeline variant (and every shadow-pass variant) is compiled before
   * gameplay.
   *
   * The legs are idle-hidden and parked below the frame at rest, so a warm-up
   * frame that did not do this compiled nothing: the first kick of a run paid
   * for the opaque pipeline and the first retract for the transparent one.
   *
   * FOUR VARIANTS, NOT TWO (2026-08-02 frame-drop pass). Each leg owns its OWN
   * material clones (see `LegState.opaqueMats` — a shared set would fade both
   * legs at once), and the right leg is MIRRORED by a negative x scale, which
   * flips the winding: three's WebGPU backend puts `frontFace` in the render
   * pipeline cache key, so left-opaque and right-opaque are genuinely different
   * pipelines. Posing "left opaque + right mid-fade" in one frame therefore
   * warmed only half of them, and the first kick of every run compiled the
   * other half inside `renderer.render` — measured with a
   * GPUDevice.createRenderPipeline probe as two compiles (`Material_0.001`,
   * `kneeCap`) landing on the frame of the run's first poke, in a 60-150 ms
   * `submit`. Two passes cover all four.
   *
   * @returns a two-pass handle; the caller renders one real frame per pass and
   *   then calls `restore`. Safe before `attach`/`load` (a no-op handle if the
   *   GLB is not loaded).
   */
  warmup(): LegWarmup {
    const noop: LegWarmup = {
      passes: 1,
      beginPass: (): void => {},
      restore: (): void => {},
    };
    if (!this.loaded) return noop;
    const culled: Mesh[] = [];
    for (const s of [this.left, this.right]) {
      s.group.visible = true;
      // Extended pose: in front of the camera, inside the shadow frustum.
      s.group.position.set(0, L.extendY, L.extendZ);
      s.group.rotation.set(L.pokeTiltRad, 0, 0);
      for (const m of s.meshes) {
        if (m.frustumCulled) {
          m.frustumCulled = false;
          culled.push(m);
        }
      }
    }
    return {
      passes: 2,
      beginPass: (p: number): void => {
        // Pass 0: left opaque + right mid-fade. Pass 1: the complement.
        const leftOpaque = p % 2 === 0;
        this.setAlpha(this.left, leftOpaque ? 1 : 0.5);
        this.setAlpha(this.right, leftOpaque ? 0.5 : 1);
      },
      restore: (): void => {
        for (const m of culled) m.frustumCulled = true;
        for (const s of [this.left, this.right]) {
          this.setAlpha(s, 1);
          s.group.visible = false;
          s.active = false;
          const baseX = legBaseX(s === this.left ? "left" : "right");
          s.group.position.set(baseX, L.restY, L.restZ);
          s.group.rotation.set(0, 0, 0);
        }
      },
    };
  }

  dispose(): void {
    for (const g of this.geometries) g.dispose();
    for (const m of this.materials) m.dispose();
    this.geometries.length = 0;
    this.materials.length = 0;
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  private updateLeg(s: LegState, dt: number): void {
    if (!s.active) {
      s.group.visible = false;
      return;
    }
    s.elapsed += dt;
    const t = clamp01(s.elapsed / s.duration);
    // ONE envelope for both outcomes: the chamber → thrust → hold → retract
    // pose (the fake knee). A whiff used to fall back to the simple one-channel
    // `pokeReach` ramp, which made a miss read as a dropped input; it now plays
    // the full kick (see `whiff` above). Pure and unit-tested (juice.test.ts /
    // pokeReach.test.ts).
    const pose = pokePose(t, s.extendFrac, s.holdFrac, poseScratch);
    let reach = pose.reach;
    let lift = pose.lift;
    let tiltMul = pose.tilt;
    reach *= s.extent;
    lift *= s.extent;
    tiltMul *= s.extent;

    if (t >= 1) {
      s.active = false;
      s.group.visible = false;
      // Hand the materials back opaque: the poke ends mid-fade (the leg is long
      // gone by then), and nothing else would ever restore them.
      this.setAlpha(s, 1);
      return;
    }

    s.group.visible = true;
    // Wrapper transform (position + tilt + aim yaw + the hip-pivot shift that
    // keeps the leg from crossing the body) — pure and headless-testable.
    poseScratchWrapper.reach = reach;
    poseScratchWrapper.lift = lift;
    poseScratchWrapper.tilt = tiltMul;
    poseScratchWrapper.aimYaw = s.aimYaw;
    poseScratchWrapper.aimSlide = s.aimX;
    poseLegWrapper(s.group, s === this.left ? "left" : "right", poseScratchWrapper);

    // Joints: absolute radians in the rig's own node space (see PokePose).
    // NOT scaled by `extent` — a point-blank kick should still chamber fully;
    // extent only shortens how far the whole leg travels.
    if (s.thigh) s.thigh.rotation.x = pose.thighRot;
    if (s.shin) s.shin.rotation.set(pose.shinRot, pose.shinYaw, 0);

    // Retract fade. The road is opaque and the leg cannot physically clear it on
    // the way down (see the retract block in juice.ts), so the last sliver of
    // road-cut leg is hidden by fading rather than by moving. `transparent` is
    // only switched on while the fade is running: at alpha 1 the material stays
    // in the opaque pass, exactly as before, with no sorting change.
    this.setAlpha(s, pose.alpha);
  }

  /**
   * Write `a` to this leg's materials (no-op if unchanged).
   *
   * NEVER mutates `transparent` and never sets `needsUpdate`: it swaps between
   * the two pre-built, pre-compiled material sets (see `LegState.fadeMats`) and
   * writes `opacity`, which three's NodeMaterialObserver already watches, so
   * the value reaches the shader as a uniform refresh with no pipeline work.
   */
  private setAlpha(s: LegState, a: number): void {
    if (a === s.alpha) return;
    s.alpha = a;
    const fading = a < 1;
    if (fading !== s.fading) {
      s.fading = fading;
      const mats = fading ? s.fadeMats : s.opaqueMats;
      for (let i = 0; i < s.meshes.length; i++) s.meshes[i].material = mats[i];
    }
    if (fading) {
      for (const m of s.fadeMats) m.opacity = a;
    }
  }

  private buildLeg(template: Group, side: "left" | "right"): LegState {
    // Clone the template. three's clone is shallow on the scene graph but
    // shares geometry (efficient — both legs share the GPU buffers).
    const clone = template.clone(true) as Group;
    clone.name = `legModel_${side}`;
    // MATERIALS, however, must NOT stay shared: the retract fade writes opacity
    // per leg, and a shared material would fade the other leg with it (and stay
    // faded, since only an active poke ever writes alpha back to 1).
    //
    // TWO sets per leg, both built HERE (never per kick): the opaque one the
    // leg wears at full alpha, and a `transparent` twin for the retract fade.
    // See `LegState.fadeMats` for why the fade must not mutate one material.
    const meshes: Mesh[] = [];
    const opaqueMats: Material[] = [];
    const fadeMats: Material[] = [];
    clone.traverse((o) => {
      const mesh = o as Mesh;
      if (!mesh.isMesh || !mesh.material) return;
      const own = (mesh.material as Material).clone();
      const fade = own.clone();
      fade.transparent = true;
      mesh.material = own;
      meshes.push(mesh);
      opaqueMats.push(own);
      fadeMats.push(fade);
      this.materials.push(own, fade); // disposed with the rest
    });
    // Mirror + foot-down-road + wrapper, exactly as the headless aim test
    // composes them (see `buildLegWrapper`).
    const wrap = buildLegWrapper(clone, side);

    // Two-bone FK rig. Mirroring is a plain x-negation on the clone root, which
    // COMMUTES with the joints' rotation about X (that rotation only touches
    // y/z), so the mirrored leg bends identically. If the GLB ever loses its
    // named nodes, warn once and degrade to the old rigid behaviour rather than
    // dropping the kick animation entirely.
    const thigh = clone.getObjectByName(THIGH_NODE) ?? null;
    const shin = clone.getObjectByName(SHIN_NODE) ?? null;
    if (!thigh || !shin) {
      console.warn(
        `[kickLeg] ${L.loadPath} is missing ${THIGH_NODE}/${SHIN_NODE}; ` +
          `falling back to a rigid (knee-locked) leg.`,
      );
    }

    const state: LegState = {
      group: wrap,
      meshes,
      opaqueMats,
      fadeMats,
      fading: false,
      alpha: 1,
      thigh,
      shin,
      elapsed: 0,
      duration: L.pokeDuration,
      extendFrac: L.pokeExtendFrac,
      holdFrac: L.pokeHoldFrac,
      extent: 1,
      active: false,
      aimX: 0,
      aimYaw: 0,
      whiff: false,
    };
    return state;
  }

  /**
   * Collect the GPU resources to free in `dispose`. Geometries and materials
   * are tracked INDEPENDENTLY: the rigged leg is two meshes (thigh + shin) that
   * share one material, so a combined "have I seen this pair?" check would drop
   * the second geometry on the floor.
   */
  private collectDisposables(root: Group): void {
    root.traverse((o) => {
      const m = o as Mesh;
      if (!m.geometry) return;
      const geo = m.geometry as BufferGeometry;
      if (!this.geometries.includes(geo)) this.geometries.push(geo);
      // Material may be an array; handle the common single-material case.
      const mat = m.material as Material;
      if (mat && !this.materials.includes(mat)) this.materials.push(mat);
    });
  }
}
